import React, { useState } from "react";
import { ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { useSubscription, ProBadge } from "./SubscriptionGate";

export const ActivityCalendar = ({ data }) => {
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  const [viewMonth, setViewMonth] = useState(currentMonth);
  const [viewYear, setViewYear] = useState(currentYear);

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDayOfWeek = new Date(viewYear, viewMonth, 1).getDay();
  const monthName = new Date(viewYear, viewMonth).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  const getActivity = (day) => {
    const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return data.find(a => a.date === dateStr);
  };

  const getCellStyle = (total) => {
    if (total === 0) return "bg-slate-50 text-slate-400";
    if (total <= 2) return "bg-sky-100 text-sky-700";
    if (total <= 4) return "bg-sky-300 text-white";
    return "bg-sky-500 text-white";
  };

  const navigateMonth = (dir) => {
    let m = viewMonth + dir;
    let y = viewYear;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    if (y > currentYear || (y === currentYear && m > currentMonth)) return;
    const monthsBack = (currentYear - y) * 12 + (currentMonth - m);
    if (monthsBack > 3) return;
    setViewMonth(m);
    setViewYear(y);
  };

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div data-testid="activity-calendar">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => navigateMonth(-1)} className="text-sky-500 hover:text-sky-700 p-1">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-sm font-medium text-sky-800" style={{ fontFamily: 'Outfit' }}>{monthName}</span>
        <button onClick={() => navigateMonth(1)} className="text-sky-500 hover:text-sky-700 p-1" disabled={viewMonth === currentMonth && viewYear === currentYear}>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {dayLabels.map(d => (
          <div key={d} className="text-center text-[10px] text-slate-400 font-medium">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: firstDayOfWeek }).map((_, i) => (
          <div key={`empty-${i}`} className="aspect-square" />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const activity = getActivity(day);
          const total = activity?.total || 0;
          const isToday = day === today.getDate() && viewMonth === currentMonth && viewYear === currentYear;
          return (
            <div
              key={`day-${day}`}
              className={`aspect-square rounded-lg flex items-center justify-center text-xs font-medium transition-colors ${getCellStyle(total)} ${isToday ? 'ring-2 ring-sky-500 ring-offset-1' : ''}`}
              title={total > 0 ? `${activity.date}: ${total} activities` : ''}
            >
              {day}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2 mt-3 text-[10px] text-slate-400 justify-end">
        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-slate-50 border border-slate-200" /> None</div>
        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-sky-100" /> Light</div>
        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-sky-300" /> Active</div>
        <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-sky-500" /> Intense</div>
      </div>
    </div>
  );
};

export const ScoreRing = ({ score, label, color, size = 80 }) => {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="currentColor" strokeWidth="4" className="text-slate-100" />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth="4"
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute flex flex-col items-center justify-center" style={{ width: size, height: size }}>
        <span className="text-lg font-semibold text-sky-900" style={{ fontFamily: 'Outfit' }}>{Math.round(score)}</span>
      </div>
      <span className="text-xs text-slate-500 mt-[-4px]">{label}</span>
    </div>
  );
};

export const JournalEntry = ({ entry, onDelete }) => {
  const moodColors = {
    great: "bg-green-100 text-green-600",
    good: "bg-sky-100 text-sky-600",
    neutral: "bg-slate-100 text-slate-600",
    tired: "bg-amber-100 text-amber-600",
    stressed: "bg-red-100 text-red-600"
  };

  return (
    <div className="flex items-start gap-3 p-3 bg-white/40 rounded-xl group" data-testid={`journal-entry-${entry.id}`}>
      <span className={`text-xs px-2 py-0.5 rounded-full ${moodColors[entry.mood_tag] || moodColors.neutral}`}>
        {entry.mood_tag}
      </span>
      <p className="text-sm text-slate-700 flex-1">{entry.text}</p>
      <button onClick={() => onDelete(entry.id)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-400 transition-all">
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export const FeatureNavCard = ({ item, navigate }) => {
  const { isPro, tier } = useSubscription();
  const showLock = item.pro && !isPro && tier !== null;
  return (
    <div 
      className="glass-card glass-card-hover p-3 sm:p-4 cursor-pointer text-center relative overflow-hidden"
      onClick={() => navigate(item.path)}
      data-testid={`nav-${item.path.slice(1)}`}
    >
      {showLock && (
        <div className="absolute top-1.5 right-1.5">
          <ProBadge className="text-[10px] px-1.5 py-0" />
        </div>
      )}
      <item.icon className={`w-6 h-6 sm:w-7 sm:h-7 mx-auto ${item.color} mb-1.5`} strokeWidth={1.5} />
      <p className="text-xs sm:text-sm text-sky-800 dark:text-sky-200 leading-tight break-words hyphens-auto" lang="auto">{item.label}</p>
    </div>
  );
};
